/**
 * Canonicalizing signature service for editor dirty-tracking.
 *
 * Editors compare a baseline signature captured at seed time against a live
 * signature of the editing session; equality means "no unsaved changes".
 * The signature is the full canonical string (recursively key-sorted JSON),
 * not a hash digest, so mismatches stay debuggable and collision-free.
 */
export class FormSignatureService {
  /**
   * Canonical signature: recursive key sort + JSON serialization.
   * Equal-by-value inputs produce identical output regardless of key
   * insertion order. Arrays keep their order — order-sensitivity is the
   * caller's semantic choice; projections pre-sort where order must not
   * matter.
   *
   * Primitive semantics match JSON: `undefined` object properties are
   * dropped, `undefined` array elements and `NaN`/`±Infinity` become
   * `null`. Top-level `undefined` throws.
   *
   * Throws on structurally non-plain values (class instances including
   * File/Date/Map/Set, functions, symbols, bigint), naming the offending
   * path (e.g. "root.file").
   */
  public static compute(value: unknown): string {
    const canonical = FormSignatureService.canonicalize(value, 'root');
    if (canonical === undefined) {
      throw new Error('FormSignatureService: cannot compute a signature for undefined at root');
    }
    return JSON.stringify(canonical);
  }

  /**
   * Returns a structure whose JSON serialization is canonical (object keys
   * recursively sorted), or `undefined` when the value would be dropped by
   * JSON serialization at this position.
   */
  private static canonicalize(value: unknown, path: string): unknown {
    if (value === undefined) {
      return undefined;
    }
    if (value === null) {
      return null;
    }
    switch (typeof value) {
      case 'string':
      case 'boolean':
        return value;
      case 'number':
        return Number.isFinite(value) ? value : null;
      case 'function':
        throw new Error(`FormSignatureService: cannot compute a signature for function at ${path}`);
      case 'symbol':
        throw new Error(`FormSignatureService: cannot compute a signature for symbol at ${path}`);
      case 'bigint':
        throw new Error(`FormSignatureService: cannot compute a signature for bigint at ${path}`);
    }
    if (Array.isArray(value)) {
      return value.map((element, index) => {
        const canonical = FormSignatureService.canonicalize(element, `${path}[${index}]`);
        return canonical === undefined ? null : canonical;
      });
    }
    const prototype = Object.getPrototypeOf(value);
    if (prototype !== Object.prototype && prototype !== null) {
      const kind = (value as object).constructor?.name ?? 'unknown';
      throw new Error(
        `FormSignatureService: cannot compute a signature for non-plain object (${kind}) at ${path}`
      );
    }
    const sorted: Record<string, unknown> = {};
    for (const key of Object.keys(value as Record<string, unknown>).sort()) {
      const canonical = FormSignatureService.canonicalize(
        (value as Record<string, unknown>)[key],
        `${path}.${key}`
      );
      if (canonical !== undefined) {
        sorted[key] = canonical;
      }
    }
    return sorted;
  }
}
